import ctl from '@netlify/classnames-template-literals'
import { ValidationError } from '@tanstack/react-form'
import { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { calculateCampaignDurationInDays } from '../../../constants'
import { Button } from '../button'
import { useDisplayDate } from '../calendar'
import { Glyphs } from '../glyphs'
import { GenericField, GenericInput, GenericPopupInput } from './generic'

export interface DateRangeFieldProps {
	label?: string
	value: [Date, Date]
	onChange: (value: [Date, Date]) => void
	hint?: ReactNode
	errors?: Array<ValidationError>
	minDate?: Date
	maxDurationDays?: number
	hideLabel?: boolean
	readOnly?: boolean
	readOnlyReason?: string
}

export function DateRangeField(
	{ label, value, onChange, hint, errors, minDate, maxDurationDays, hideLabel, readOnly, readOnlyReason }:
		DateRangeFieldProps,
) {
	const { t } = useTranslation('common')
	const [startDate, endDate] = value
	const [currentMonth, setCurrentMonth] = useState(() => {
		// Initialize to the selected start date's month, or current month if no date selected
		return startDate ? new Date(startDate.getFullYear(), startDate.getMonth()) : new Date()
	})
	const displayDate = useDisplayDate()

	// Update currentMonth when startDate changes (e.g., when value prop changes externally)
	useEffect(() => {
		if (startDate) {
			setCurrentMonth(new Date(startDate.getFullYear(), startDate.getMonth()))
		}
	}, [startDate])

	const createDateAtStartOfDay = (year: number, month: number, day: number) => {
		const date = new Date(year, month, day, 0, 0, 0, 0)
		return date
	}

	const createDateAtEndOfDay = (year: number, month: number, day: number) => {
		const date = new Date(year, month, day, 23, 59, 59, 999)
		return date
	}

	const isInEndDateSelection = startDate && (!endDate || startDate.toDateString() === endDate.toDateString())

	const handleDateClick = (day: number) => {
		if (readOnly) {
			return
		}

		const selectedDateAsStart = createDateAtStartOfDay(
			currentMonth.getFullYear(),
			currentMonth.getMonth(),
			day,
		)
		const selectedDateAsEnd = createDateAtEndOfDay(currentMonth.getFullYear(), currentMonth.getMonth(), day)

		// Check if date is before minDate
		if (minDate) {
			const min = new Date(minDate)
			min.setHours(0, 0, 0, 0)
			if (selectedDateAsStart < min) {
				return
			}
		}

		if (!startDate) {
			onChange([selectedDateAsStart, selectedDateAsEnd])
		} else if (isInEndDateSelection) {
			if (selectedDateAsStart.toDateString() === startDate.toDateString()) {
				return
			}
			if (selectedDateAsStart < startDate) {
				let newEnd = endDate
				if (maxDurationDays) {
					const maxAllowedEnd = new Date(selectedDateAsStart)
					maxAllowedEnd.setDate(maxAllowedEnd.getDate() + maxDurationDays - 1)
					maxAllowedEnd.setHours(23, 59, 59, 999)
					if (newEnd > maxAllowedEnd) {
						newEnd = maxAllowedEnd
					}
				}
				onChange([selectedDateAsStart, newEnd])
			} else {
				onChange([startDate, selectedDateAsEnd])
			}
		} else {
			onChange([selectedDateAsStart, selectedDateAsEnd])
		}
	}

	const handleMonthChange = (delta: number) => {
		const newMonth = currentMonth.getMonth() + delta
		if (newMonth > 11) {
			setCurrentMonth(new Date(currentMonth.getFullYear() + 1, 0))
		} else if (newMonth < 0) {
			setCurrentMonth(new Date(currentMonth.getFullYear() - 1, 11))
		} else {
			setCurrentMonth(new Date(currentMonth.getFullYear(), newMonth))
		}
	}

	const getDaysInMonth = (date: Date) => {
		return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
	}

	const getFirstDayOfMonth = (date: Date) => {
		const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay()
		// Convert Sunday (0) to 7, and adjust others to Mon=1, Tue=2, etc.
		return day === 0 ? 6 : day - 1
	}

	const isDateInRange = (day: number): boolean => {
		if (!startDate || !endDate) { return false }
		const date = createDateAtStartOfDay(currentMonth.getFullYear(), currentMonth.getMonth(), day)
		return date > startDate && date < endDate
	}

	const isDateSelected = (day: number): boolean => {
		const dateAtStart = createDateAtStartOfDay(currentMonth.getFullYear(), currentMonth.getMonth(), day)
		const dateAtEnd = createDateAtEndOfDay(currentMonth.getFullYear(), currentMonth.getMonth(), day)
		const isStart = startDate && dateAtStart.toDateString() === startDate.toDateString()
		const isEnd = endDate && dateAtEnd.toDateString() === endDate.toDateString()

		return isStart || isEnd
	}

	const maxEndDate = (() => {
		if (!maxDurationDays || !startDate) { return null }
		const max = new Date(startDate)
		max.setDate(max.getDate() + maxDurationDays - 1)
		max.setHours(23, 59, 59, 999)
		return max
	})()

	const isDateDisabled = (day: number): boolean => {
		const date = createDateAtStartOfDay(currentMonth.getFullYear(), currentMonth.getMonth(), day)
		if (minDate) {
			const min = new Date(minDate)
			min.setHours(0, 0, 0, 0)
			if (date < min) { return true }
		}
		if (maxDurationDays && startDate && isInEndDateSelection) {
			if (maxEndDate) {
				const maxDay = new Date(maxEndDate)
				maxDay.setHours(0, 0, 0, 0)
				if (date > maxDay) { return true }
			}
			const minAllowedStart = new Date(startDate)
			minAllowedStart.setDate(minAllowedStart.getDate() - maxDurationDays + 1)
			minAllowedStart.setHours(0, 0, 0, 0)
			if (date < minAllowedStart) { return true }
		}
		return false
	}

	const applyPreset = (daysToAdd: number) => {
		const effectiveDays = maxDurationDays ? Math.min(daysToAdd, maxDurationDays - 1) : daysToAdd
		const candidateStart = createDateAtStartOfDay(
			new Date().getFullYear(),
			new Date().getMonth(),
			new Date().getDate(),
		)
		let from = candidateStart
		if (minDate) {
			const minStart = new Date(minDate)
			minStart.setHours(0, 0, 0, 0)
			from = candidateStart < minStart ? minStart : candidateStart
		}
		const to = new Date(from)
		to.setDate(to.getDate() + effectiveDays)
		to.setHours(23, 59, 59, 999)
		onChange([from, to])
		setCurrentMonth(new Date(from.getFullYear(), from.getMonth()))
	}

	const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })
	const daysInMonth = getDaysInMonth(currentMonth)
	const firstDay = getFirstDayOfMonth(currentMonth)
	const days = Array.from({ length: daysInMonth }, (_, i) => i + 1)

	const duration = startDate && endDate
		? calculateCampaignDurationInDays(startDate, endDate)
		: null

	const presets = [
		{ days: 6, label: t('dateRange.oneWeek') },
		{ days: 13, label: t('dateRange.twoWeeks') },
		{ days: 20, label: t('dateRange.threeWeeks') },
		{ days: 27, label: t('dateRange.fourWeeks') },
	].filter(p => !maxDurationDays || p.days < maxDurationDays)

	return (
		<GenericField label={label} errors={errors} hint={hint} hideLabel={hideLabel}>
			<GenericPopupInput
				triggerWrapperClassName="w-full"
				useReferenceWidth={false}
				trigger={
					<GenericInput
						isGenerating={false}
						isLoading={false}
						isDisabled={false}
					>
						{({ className }) => (
							<>
								<div
									className={`${className} w-full text-base font-normal flex items-center justify-between`}
								>
									{startDate && endDate
										? `${displayDate(startDate)} - ${displayDate(endDate)}`
										: t('dateRange.selectDates')}

									<div className="pr-1.5">
										<Glyphs.core.CalendarBlankIcon />
									</div>
								</div>
							</>
						)}
					</GenericInput>
				}
			>
				<div className="space-y-4 min-w-[200px]">
					{/* Month Navigation */}
					<div className="flex items-center justify-between">
						<h3 className="text-base font-semibold text-grey-900">{monthName}</h3>
						<div className="flex gap-2">
							<button
								onClick={() => handleMonthChange(-1)}
								className="text-grey-600 cursor-pointer p-2 hover:text-grey-900 rounded hover:bg-grey-50"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 16 16"
									fill="currentColor"
									className="size-4"
								>
									<path
										fillRule="evenodd"
										d="M14 8a.75.75 0 0 1-.75.75H4.56l3.22 3.22a.75.75 0 1 1-1.06 1.06l-4.5-4.5a.75.75 0 0 1 0-1.06l4.5-4.5a.75.75 0 0 1 1.06 1.06L4.56 7.25h8.69A.75.75 0 0 1 14 8Z"
										clipRule="evenodd"
									/>
								</svg>
							</button>
							<button
								onClick={() => handleMonthChange(1)}
								className="text-grey-600 cursor-pointer p-2 hover:text-grey-900 rounded hover:bg-grey-50"
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 16 16"
									fill="currentColor"
									className="size-4"
								>
									<path
										fillRule="evenodd"
										d="M2 8a.75.75 0 0 1 .75-.75h8.69L8.22 4.03a.75.75 0 0 1 1.06-1.06l4.5 4.5a.75.75 0 0 1 0 1.06l-4.5 4.5a.75.75 0 0 1-1.06-1.06l3.22-3.22H2.75A.75.75 0 0 1 2 8Z"
										clipRule="evenodd"
									/>
								</svg>
							</button>
						</div>
					</div>

					{/* Day Headers */}
					<div className="grid grid-cols-7 gap-1">
						{[
							t('dateRange.dayMo'),
							t('dateRange.dayTu'),
							t('dateRange.dayWe'),
							t('dateRange.dayTh'),
							t('dateRange.dayFr'),
							t('dateRange.daySa'),
							t('dateRange.daySu'),
						].map((day, i) => (
							<div
								key={i}
								className="text-xs text-grey-800 pb-2 font-medium text-center border-b border-grey-100"
							>
								{day}
							</div>
						))}
					</div>

					{/* Calendar Days */}
					<div className="grid grid-cols-7 gap-1">
						{Array.from({ length: firstDay }).map((_, i) => <div key={`empty-${i}`} />)}
						{days.map((day) => {
							const isDisabled = isDateDisabled(day)
							const isSelected = isDateSelected(day)
							const inRange = isDateInRange(day)

							return (
								<button
									key={day}
									onClick={() => handleDateClick(day)}
									disabled={isDisabled}
									className={ctl(`
										aspect-square rounded text-sm font-medium transition-colors
										flex items-center justify-center
										p-3
										${
										isSelected
											? 'bg-brand-500 text-white'
											: inRange
											? 'bg-brand-100 text-grey-900'
											: readOnly
											? 'text-grey-900'
											: 'hover:bg-grey-50 text-grey-900 cursor-pointer'
									}
										${
										isDisabled
											? 'opacity-40 cursor-not-allowed'
											: readOnly
											? 'cursor-not-allowed'
											: ''
									}
									`)}
								>
									{day}
								</button>
							)
						})}
					</div>

					{/* Selected Dates Display */}
					{startDate && duration !== null && (
						<div className="p-3 bg-brand-50 border border-brand-200 rounded space-y-2">
							<div className="flex justify-between items-start">
								<div>
									<p className="text-xs text-grey-600">{t('dateRange.campaignDuration')}</p>
									<p className="text-xl font-bold text-brand-700">
										{t('dateRange.durationDays', { count: duration })}
									</p>
								</div>
								<div className="text-right text-sm">
									<p className="text-grey-600">
										{displayDate(startDate)}
									</p>
									{endDate && startDate.toDateString() !== endDate.toDateString() && (
										<>
											<p className="text-grey-400">{t('dateRange.to')}</p>
											<p className="text-grey-600">
												{displayDate(endDate)}
											</p>
										</>
									)}
								</div>
							</div>
							{maxDurationDays && (
								<p className="text-xs text-grey-500">
									{t('dateRange.maxDurationHint', { count: maxDurationDays })}
								</p>
							)}
						</div>
					)}

					{/* Readonly warning; w-0 + min-w-full wraps the text instead of letting it widen the popup */}
					{readOnly && (
						<div className="py-0 text-center w-0 min-w-full">
							<p className="text-xs font-medium text-grey-600">
								{readOnlyReason ?? t('dateRange.readonlyWarning')}
							</p>
						</div>
					)}

					{/* Quick Presets */}
					{!readOnly && presets.length > 0 && (
						<div className="space-y-2 pt-2 border-t border-grey-100">
							<p className="text-sm font-medium text-grey-700">{t('dateRange.quickPresets')}</p>
							<div className="grid grid-cols-2 gap-2">
								{presets.map(preset => (
									<Button
										key={preset.days}
										type="subtle"
										size="short"
										fullWidth
										onClick={() => applyPreset(preset.days)}
									>
										{preset.label}
									</Button>
								))}
							</div>
						</div>
					)}
				</div>
			</GenericPopupInput>
		</GenericField>
	)
}
